'use client';

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { AlertTriangle, ChevronDown, Crosshair, MapPinned, Route, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, getApiErrorMessage } from '@/lib/api';
import { TERMS, plural } from '@/lib/copy';
import { COVERAGE_UNREAD_COPY } from '@/lib/copy/status';
import { toast } from 'sonner';
import { storedWaypointsToPlan, type StoredWaypoint } from '@/lib/mission-planning';
import { LAUNCH_PICKER_STATUSES, missionLaunchBlockReason } from '@/lib/mission-launchable';
import type { DockMissionRequest } from '@/types/dock';
import type { DockMissionPlanController } from '@/hooks/useDockMissionPlan';
import type { LiveDrone } from '@/hooks/useWebSocket';
import type { LaunchMode } from '@/components/dashboard/DockBayRow';
import { AltitudeControl } from '@/components/dashboard/AltitudeControl';
import type { AltitudeProfileStatus } from '@/hooks/useAltitudeProfile';
import {
  clampAltitudeMeters,
  evaluateCeiling,
  evaluateStationAltitude,
  formatAltitude,
  toDisplay,
  type AltitudeProfile,
  type AltitudeUnit,
  type StationAltitudeBand,
} from '@/lib/altitude';
import { launchAdvisoryNote } from '@/lib/launch-request';
import { launchToastId, registerOwnLaunch } from '@/lib/launch-tracking';
import { useEnclosingStageInsets } from '@/app/(dashboard)/_cop/useStageInsets';
import { stationEnduranceWarningHead, useStationEndurance } from '@/hooks/useStationEndurance';
import { StationEnduranceNotice } from './StationEnduranceNotice';

interface DockLaunchOverlayProps {
  dockId: string;
  /** Live telemetry for the target drone — drives the callsign shown in the overlay. */
  assignedDrone: LiveDrone | null;
  /** Drone picked from a dock bay row. Sent as `drone_id` so the backend launches THAT bay. */
  targetDroneId: string | null;
  launchMode: LaunchMode;
  isOnline: boolean;
  /** Shared draft route — owned by the COP view, also rendered on the map. */
  plan: DockMissionPlanController;
  /** Dock registry position — the takeoff-in-place origin when the aircraft
   *  has no live fix (e.g. GPS-dark inside a closed bay). */
  dockPosition?: { lat: number; lng: number } | null;
  /** Org altitude configuration: presets, ceiling, waiver, RTH, display unit. */
  altitudeProfile: AltitudeProfile;
  /** Label for whoever owns the ceiling, e.g. "FAA Part 107". Null if unknown. */
  ceilingAuthority: string | null;
  /**
   * When 'error', the profile above is a platform fallback rather than this
   * org's configuration, and the card says so instead of implying otherwise.
   */
  altitudeProfileStatus?: AltitudeProfileStatus;
  /** Persists a new org-wide default altitude. Null/omitted when unavailable. */
  onSaveDefaultAltitude?: ((meters: number) => Promise<void>) | null;
  /**
   * A launch was accepted (HTTP 202), reporting which aircraft is flying it and
   * which mission row records it. Fires before `onClose`, so the COP can pin the
   * aircraft's details panel to THIS sortie rather than guessing from drone id.
   * Either argument may be null — the launch is still real, and the COP still
   * needs to know one happened even when it cannot be attributed exactly.
   */
  onLaunched?: (droneId: string | null, missionId: string | null) => void;
  /**
   * The launch was refused. The backend may already have written (and then
   * cancelled) a mission row for it, so the COP needs to resync rather than keep
   * serving whatever it last cached for this aircraft.
   */
  onLaunchFailed?: () => void;
  onClose: () => void;
}

/** The 202 body of POST /api/docks/:id/mission. `mission_id` is nullable: the
 *  backend logs and swallows a bookkeeping failure rather than aborting a
 *  cleared launch, so an aircraft can genuinely be flying with no mission row. */
interface DockMissionResponse {
  dock_id: string;
  drone_id: string;
  mission_id: string | null;
  /**
   * `accepted` since #418, not `dispatched`. The door now answers as soon as the DOCK BAY is
   * confirmed open - before the aircraft has been commanded anything - so that the wait for it
   * to record a home point on the open tray is not bounded by the browser's 55s abort. A
   * synchronous `dispatched` still arrives from the airborne path, which has no bay to open.
   */
  status: string;
  /**
   * Present on an `accepted` answer: what to follow to find out how the launch actually ended.
   *
   * `useCopLaunchProgress` at COP level carries the toast this card opens under this id through
   * to its terminal frame, and `GET /api/launches/:launch_id` answers the same question after a
   * reload. Absent on a synchronous `dispatched`, and absent from an older server.
   */
  launch_id?: string;
  /**
   * Non-blocking notes about the launch that just happened — airspace conflicts the gate let
   * through, and the coverage-station altitude mismatch (#469).
   *
   * `src/api/docks.ts` has returned these on the 202 since the airspace severity split, and
   * QuickDispatchDialog, CopMissionsPanel and useMissionCommit all render them. THIS card was
   * the one launch door that dropped them on the floor — which for #469 would mean the server's
   * own backstop, the thing that covers a failed coverage read here, never reached anyone.
   */
  advisories?: unknown;
}

/** A saved mission summary as returned by GET /api/missions.
 *
 *  Carries the four fields `missionLaunchBlockReason` reads. They were already in the payload
 *  (`findByOrg` selects `m.*` and decorates it) — the row type simply dropped them, so the
 *  picker could not tell a launchable mission from one the server would refuse. */
interface SavedMissionSummary {
  id: string;
  name: string;
  status: string;
  type?: string | null;
  hover_seconds?: number | null;
  waypoint_count?: number;
  coverage_held_open?: boolean;
  /**
   * Coverage is attached to this mission — so it has a station altitude, and the ALT below has
   * to answer to it (#469). Distinct from `coverage_held_open`, which is the narrower "the only
   * reason this mission is still in_progress"; a draft mission with coverage attached is
   * `coverage_attached` and not held open, and it is exactly the case this card launches.
   */
  coverage_attached?: boolean;
}

/** GET /api/coverage/:missionId — only the two fields the ALT control has to answer to. */
interface CoverageStationResponse {
  coverage: { station_altitude_m: number };
  altitude_tolerance_m: number;
}

/** GET /api/missions/:id response — mission fields plus embedded waypoints. */
interface MissionWithWaypoints {
  id: string;
  name: string;
  waypoints: StoredWaypoint[];
  /**
   * Decorated by the same `withCoverageFacts` the list uses, at `'listed'` scope for this one
   * mission — so it is a SECOND chance at a fact the picker's copy may have missed (#650).
   *
   * The picker list is fetched once per launch intent and has no refresh path, so a lookup that
   * threw at the moment the overlay armed would otherwise mark every mission selected for the
   * rest of that session unreadable, with no way back but closing and reopening the card.
   */
  coverage_attached?: boolean;
}

type Step = 'target' | 'review';

const MIN_WAYPOINTS = 1;
const MAX_WAYPOINTS = 100;
/** Ties the ALT disclosure button to the panel it reveals, for assistive tech. */
const ALTITUDE_PANEL_ID = 'dock-launch-altitude-panel';

/**
 * How long the coverage-station read may hold the review card closed (#469).
 *
 * `api.get` carries no default timeout — only `post` does — so without this a stalled read
 * leaves the operator on a dimmed mission picker with no spinner and no exit but the X. Short,
 * because the answer is an annotation: losing it costs a warning line, and waiting for it costs
 * the launch.
 */
const COVERAGE_READ_TIMEOUT_MS = 5_000;

const MODE_LABEL: Record<LaunchMode, string> = {
  takeoff: 'Takeoff',
  point: 'To Point',
  route: 'To Route',
  mission: 'Mission',
};

function formatDistance(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
}

function formatDuration(s: number): string {
  const total = Math.round(s);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')} min`;
}

const CARD_CLASS =
  'pointer-events-auto w-[min(23.5rem,calc(100vw-2rem))] rounded-lg border border-subtle bg-popover/95 shadow-2xl backdrop-blur-sm';

function OverlayHeader({ title, onCancel }: { title: string; onCancel: () => void }) {
  return (
    <div className="flex items-center gap-2 border-b divider px-3 py-2">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{title}</span>
      <button
        type="button"
        onClick={onCancel}
        aria-label="Cancel launch"
        className="ml-auto rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

/**
 * Compact map overlay driving the launch sequence — Target/Route/Mission →
 * Review → Launch — for a drone picked from a Dock Sidebar bay row.
 *
 * Altitude is chosen inside this card rather than in a dialog: the operator has
 * just placed a target on the map, and a modal would cover the very thing they
 * are reasoning about. The card opens with the org's configured default already
 * selected, so a routine launch is one click and the picker is only expanded
 * when the altitude actually needs to change.
 */
export function DockLaunchOverlay({
  dockId,
  assignedDrone,
  targetDroneId,
  launchMode,
  isOnline,
  plan,
  dockPosition = null,
  altitudeProfile,
  ceilingAuthority,
  altitudeProfileStatus = 'ready',
  onSaveDefaultAltitude,
  onLaunched,
  onLaunchFailed,
  onClose,
}: DockLaunchOverlayProps) {
  /**
   * Every card below is centred with `inset-x-0 ... justify-center`, which
   * centres it on the CANVAS - and the canvas now runs underneath the floating
   * side panel. This is the same defect `CopBottomStack` was fixed for, on a
   * surface that never got the same treatment: the launch card is `z-30` against
   * the panel's `z-20`, so it wins and draws on top of it.
   *
   * It is not a corner case. `launchOverlay` only renders for a selected dock,
   * and a selected dock is exactly what makes the dock sheet the active one, so
   * the panel is ALWAYS open behind this card. On a 1024 px screen the 376 px
   * card already overlaps the sheet by ~28 px at the default width.
   *
   * This is golden-flow step 6 - the card where an operator confirms an altitude
   * and commits an aircraft - so it is the last one that should be drawn half
   * over something else.
   */
  const stageInsets = useEnclosingStageInsets();
  const centredOverVisibleMap: CSSProperties | undefined =
    stageInsets.left > 0 || stageInsets.right > 0
      ? { paddingLeft: stageInsets.left, paddingRight: stageInsets.right }
      : undefined;

  const [step, setStep] = useState<Step>('target');
  const [altitudeMeters, setAltitudeMeters] = useState(() => altitudeProfile.default_m);
  const [altitudeOpen, setAltitudeOpen] = useState(false);
  const [unit, setUnit] = useState<AltitudeUnit>(altitudeProfile.unit);
  const [savingDefault, setSavingDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);
  /** Set when the dock refused to confirm its bay and the operator must decide. */
  const [bayPrompt, setBayPrompt] = useState<
    { body: DockMissionRequest; successLabel: string; acceptedLabel: string; reason: string } | null
  >(null);

  const [missions, setMissions] = useState<SavedMissionSummary[]>([]);
  const [missionsLoading, setMissionsLoading] = useState(false);
  /**
   * The last load failed. Tracked separately from `missions.length === 0` because the two mean
   * opposite things to an operator: an empty list is "nothing is planned", a failed load is
   * "we do not know what is planned". Rendering the second as the first is the mistake
   * `CopMissionsPanel` already names — "there is NO data, which must never render as 'no
   * missions'" — and at a dock it reads as the org's plan having been lost.
   */
  const [missionsLoadError, setMissionsLoadError] = useState(false);
  const [selectedMission, setSelectedMission] = useState<SavedMissionSummary | null>(null);
  const [missionLoadBusy, setMissionLoadBusy] = useState(false);
  /**
   * The selected mission's coverage station band, or null when it has no coverage — and null
   * also when the coverage read failed (#469).
   *
   * The two are not distinguished on purpose. Everything downstream is advice, so a failed read
   * costs an annotation and nothing else; the server checks the same thing again at launch and
   * says so on the 202, which is the backstop that makes silence here acceptable.
   */
  const [stationBand, setStationBand] = useState<StationAltitudeBand | null>(null);
  /**
   * WHY ALT WAS NOT CHECKED AGAINST A STATION — or `null` when the question was answered (#469).
   *
   * Tracked separately from `stationBand === null`, which also means "no coverage at all", for
   * the reason this card already states 180 lines up about `missionsLoadError`: an empty answer
   * and a failed answer mean opposite things, and rendering the second as the first tells the
   * operator a check passed when it never ran. The server re-checks on the 202, but that arrives
   * after the aircraft is committed — the same "found out too late" shape #469 exists to remove.
   *
   * TWO REASONS, NOT ONE, AND THEY ARE DIFFERENT SENTENCES (#650). `'read_failed'` is the #469
   * case: the mission definitely HAS coverage and `/api/coverage/:id` could not say where its
   * station is. `'coverage_unread'` is the newer one: the mission list itself came back without
   * `coverage_attached`, so we do not know there is a station at all, and saying "station
   * altitude unavailable" there would send the operator looking for a rotation that may not
   * exist. `missionDeleteBlocker` ruled on this identical fork one layer down and split the
   * sentence for the same reason; "Coverage unread" is the head-term `copy/status.ts` fixes for
   * this state across every surface that can show it.
   *
   * A union rather than two booleans because they are mutually exclusive by construction — one
   * load answers the coverage question exactly once, one way.
   */
  const [stationBandGap, setStationBandGap] = useState<'read_failed' | 'coverage_unread' | null>(null);
  /**
   * Whether the aircraft could hold a rotatable station, for the selected covered mission (#753).
   *
   * Asked BESIDE the station read, never awaited by the mission load (D7), and keyed by mission so
   * an answer that lands after another pick is never drawn under it.
   */
  const { check: checkStationEndurance, stateFor: stationEnduranceFor } = useStationEndurance();
  /**
   * One LOAD of a covered mission - a fresh object per load, so reselecting the same mission is a
   * new load and asks again, while a late aircraft id within the same load does not start over.
   */
  const [enduranceLoad, setEnduranceLoad] = useState<{ missionId: string } | null>(null);
  /**
   * The current altitude was put there by the station prefill, not by the operator.
   *
   * State rather than a ref because two things render off it: the confirming line that tells the
   * operator why ALT changed under them, and the suppression of "Make <alt> the default for all
   * operators" — which must not offer to write ONE MISSION'S station altitude into the org-wide
   * default just because the prefill flipped `altitudeTouchedRef`.
   */
  const [altitudePrefilled, setAltitudePrefilled] = useState(false);

  const droneLabel = assignedDrone?.callsign?.trim()
    || (targetDroneId ? targetDroneId.slice(-6).toUpperCase() : 'DRONE');

  const refreshMissions = useCallback(async () => {
    setMissionsLoading(true);
    setMissionsLoadError(false);
    try {
      // Asks for the three statuses that MAY be launchable rather than for everything the org
      // has ever saved. `completed` and `cancelled` are terminal — nothing can make them fly
      // again — so they are dropped at the source instead of being rendered and then greyed
      // out, which on a busy board would bury the launchable rows under old sorties.
      // `in_progress` is fetched because the coverage hold can make one launchable (#386);
      // `missionLaunchBlockReason` decides each row from there.
      const res = await api.get<{ data: SavedMissionSummary[] }>(
        `/api/missions?status=${LAUNCH_PICKER_STATUSES.join(',')}`,
      );
      setMissions(res.data);
    } catch (e) {
      setMissionsLoadError(true);
      toast.error(getApiErrorMessage(e, 'Failed to load missions'));
    } finally {
      setMissionsLoading(false);
    }
  }, []);

  // Opening the overlay for a fresh launch intent (new mode, or a different
  // drone) arms the matching flow exactly once. Keyed on the intent so
  // re-renders never re-trigger it — mirrors the guard DockMissionPanel used.
  // The altitude profile is read here rather than in deps so a config fetch
  // landing mid-flow can never re-arm the map and drop the operator's target.
  const initedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${launchMode}:${targetDroneId ?? ''}`;
    if (initedRef.current === key) return;
    initedRef.current = key;
    const seedAltitude = altitudeProfile.default_m;
    setStep(launchMode === 'takeoff' ? 'review' : 'target');
    setRouteError(null);
    setSelectedMission(null);
    // A point or route launch has no mission to judge, so no endurance question may stay open.
    setEnduranceLoad(null);
    setAltitudeOpen(false);
    setAltitudeMeters(seedAltitude);
    setUnit(altitudeProfile.unit);
    altitudeTouchedRef.current = false;
    seededDefaultRef.current = seedAltitude;
    if (launchMode === 'point') {
      plan.beginPoint(seedAltitude);
    } else if (launchMode === 'route') {
      plan.beginRoute(seedAltitude);
    } else if (launchMode === 'mission') {
      void refreshMissions();
    }
    // takeoff: no map target — origin is the aircraft/dock position at dispatch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchMode, targetDroneId]);

  // The org profile can land after the overlay already armed on fallback values.
  // Re-seed from it — but only while the operator hasn't touched the altitude,
  // so a slow config fetch can never move a number they deliberately set.
  const altitudeTouchedRef = useRef(false);
  const seededDefaultRef = useRef(altitudeProfile.default_m);
  useEffect(() => {
    if (altitudeTouchedRef.current) return;
    if (seededDefaultRef.current === altitudeProfile.default_m) return;
    seededDefaultRef.current = altitudeProfile.default_m;
    setAltitudeMeters(altitudeProfile.default_m);
    setUnit(altitudeProfile.unit);
  }, [altitudeProfile.default_m, altitudeProfile.unit]);

  const routeBlockReason = (() => {
    if (plan.waypoints.length < MIN_WAYPOINTS) return `Add at least ${MIN_WAYPOINTS} waypoint on the map`;
    if (plan.waypoints.length > MAX_WAYPOINTS) return `Maximum ${MAX_WAYPOINTS} waypoints`;
    const firstError = plan.violations.find((v) => v.severity === 'error');
    if (firstError) return firstError.message;
    return null;
  })();

  const pointBlockReason = plan.waypoints.length !== 1 ? 'Pick one target point on the map' : null;

  const openReviewStep = useCallback(() => setStep('review'), []);

  /**
   * The one write path for altitude. Marks the value as operator-chosen so a
   * late config fetch won't overwrite it, and keeps the point-mode draft
   * waypoint's own `alt` in step with the dispatched value — the two are read
   * by different code paths (`plan.violations` vs the POST body) and drifting
   * apart would validate one altitude while flying another.
   *
   * Declared ABOVE `loadMission`, which depends on it for the coverage-station prefill (#469).
   * These are `const` bindings, so a `useCallback` dependency array naming one declared further
   * down throws on the first render rather than on the first click.
   *
   * `source` separates the two writers. Both must set `altitudeTouchedRef` — a late config fetch
   * must not overwrite a prefilled station altitude any more than a typed one — but only the
   * operator's own choice may re-enable "make this the org default", and only the prefill may
   * claim the confirming line that explains why ALT moved on its own.
   */
  const changeAltitude = useCallback(
    (meters: number, source: 'operator' | 'prefill' = 'operator') => {
      const next = clampAltitudeMeters(meters, altitudeProfile);
      altitudeTouchedRef.current = true;
      setAltitudePrefilled(source === 'prefill');
      setAltitudeMeters(next);
      if (launchMode === 'point' && plan.waypoints.length === 1) {
        plan.updateAt(0, { alt: next });
      }
    },
    [altitudeProfile, launchMode, plan],
  );

  // TO POINT: a single map click already replaces the target (useDockMissionPlan's
  // ADD case) — this is the one new behavior needed: auto-advance to review
  // instead of leaving the map armed for another click.
  useEffect(() => {
    if (launchMode !== 'point' || step !== 'target') return;
    if (plan.planningKind !== 'point' || !plan.planOnMap) return;
    if (plan.waypoints.length !== 1) return;
    plan.stopPlanOnMap();
    openReviewStep();
  }, [launchMode, step, plan, openReviewStep]);

  // TO ROUTE: double-click already locks the route (DroneMap's plan-mode click
  // arbiter calls stopPlanOnMap on dblclick). Advance only once a valid route
  // exists; an accidental double-click with nothing drawn just re-arms drawing.
  useEffect(() => {
    if (launchMode !== 'route' || step !== 'target') return;
    if (plan.planningKind !== 'route') return;
    if (plan.planOnMap) {
      setRouteError(null);
      return;
    }
    if (plan.waypoints.length === 0) {
      plan.togglePlanOnMap();
      return;
    }
    if (routeBlockReason) {
      setRouteError(routeBlockReason);
      return;
    }
    setRouteError(null);
    openReviewStep();
  }, [launchMode, step, plan, routeBlockReason, openReviewStep]);

  // The id, not the live object: `assignedDrone` is a fresh telemetry snapshot every tick, and
  // depending on it would rebuild `loadMission` at telemetry rate for one string it reads.
  const enduranceDroneId = targetDroneId ?? assignedDrone?.drone_id ?? null;

  // Asked for the covered mission on the card, and asked AGAIN when the aircraft behind it changes:
  // a card opened before the live aircraft arrived must not keep saying none was identified.
  useEffect(() => {
    // Runs once per load - reselecting a mission is a new load, and how an operator asks again - and
    // once per change of aircraft within it. Both are new questions, so neither reuses an answer.
    if (enduranceLoad) void checkStationEndurance(enduranceLoad.missionId, enduranceDroneId);
  }, [enduranceLoad, enduranceDroneId, checkStationEndurance]);

  const loadMission = useCallback(
    async (mission: SavedMissionSummary) => {
      setMissionLoadBusy(true);
      try {
        const res = await api.get<{ data: MissionWithWaypoints }>(`/api/missions/${mission.id}`);
        const waypoints = res.data.waypoints ?? [];
        if (waypoints.length === 0) {
          toast.error('That mission has no waypoints');
          return;
        }
        plan.replaceWaypoints(storedWaypointsToPlan(waypoints));
        setSelectedMission(mission);
        // #469: a covered mission's ALT has an answer that is already known, so offer it rather
        // than making the operator remember it. Read AFTER the waypoints are committed and
        // guarded on its own, because a coverage read that fails must not fail the mission load
        // — the sortie is still perfectly launchable, it just launches without the annotation.
        setStationBand(null);
        setStationBandGap(null);
        setEnduranceLoad(null);
        /**
         * THREE STATES, BECAUSE THE FIELD HAS THREE (#650). This used to be `if
         * (mission.coverage_attached)`, so an UNREAD field skipped the whole branch — no read
         * attempted, no band, no prefill, and `stationBandGap` left `null`, which renders as
         * silence. The card then looked exactly like a mission with no coverage while the notice
         * fifteen lines below exists precisely to stop that: silence here tells the operator the
         * altitude was checked against the station when it never was.
         *
         * THE DETAIL RESPONSE ANSWERS FIRST. `res.data` was decorated by the same server helper
         * moments ago, so when the picker's copy is unread and this one is not, the newer answer
         * wins — `??`, so a definite `false` from the detail read is honoured and only absence
         * falls through. This is not a second SOURCE disagreeing with the first; it is the same
         * source asked again, which is the recovery `withCoverageFacts` already performs
         * internally when a failed `attached` lookup can still be answered from `held`.
         *
         * `undefined` STILL DOES NOT ATTEMPT THE COVERAGE READ. `/api/coverage/:id` answers 404
         * for an uncovered mission, which is indistinguishable here from the covered-but-
         * unreadable case and would land in the same `catch` — asserting #469's "the station could
         * not be read" over a mission that may have no station at all. The honest answer is the
         * one we actually have: nobody could tell us whether there is coverage.
         */
        const coverageAttached = res.data.coverage_attached ?? mission.coverage_attached;
        if (coverageAttached === undefined) {
          setStationBandGap('coverage_unread');
        } else if (coverageAttached) {
          // The effect above asks, even with no aircraft id yet, so the card says it was not
          // checked instead of saying nothing - silence reads as a pass.
          setEnduranceLoad({ missionId: mission.id });
          try {
            const cov = await api.get<{ data: CoverageStationResponse }>(
              `/api/coverage/${mission.id}`,
              { signal: AbortSignal.timeout(COVERAGE_READ_TIMEOUT_MS) },
            );
            const band = {
              stationAltitudeM: cov.data.coverage.station_altitude_m,
              toleranceM: cov.data.altitude_tolerance_m,
            };
            setStationBand(band);

            // ONLY WHEN THE FLATTEN IS A NO-OP, and this is the sharp edge of the whole feature.
            //
            // The card's ALT replaces the altitude of EVERY waypoint, and the station altitude
            // is the LAST waypoint's (`stationForMission` takes the hold point, and
            // `attachCoverage` defaults the column to it). A covered `hover` mission may hold
            // many waypoints — the #469 sortie itself had five — so a route that transits at
            // 80 m and holds at 20 m has `station_altitude_m` = 20. Prefilling it there would
            // fly all four transit legs sixty metres lower than planned, silently, behind a
            // green button that reads "Launch @ 66 ft" and a station notice that stays quiet
            // because 20 m is dead-on the band. That is a worse failure than the one this
            // feature exists to prevent, and the prefill is precisely what would let the
            // operator skip past the `missionPlannedRange` line that is the only other signal.
            //
            // So: when the stored route already flies one altitude, offering the station value
            // changes nothing except the number, which is the case the prefill is for. When the
            // route has a spread, the band is still set — the notice and the off-station label
            // still work — but the altitude is left alone for the operator to choose knowingly.
            const alts = waypoints
              .map((w) => w.altitude)
              .filter((a): a is number => a !== null && Number.isFinite(a));
            const uniform = alts.length > 0 && Math.min(...alts) === Math.max(...alts);
            if (uniform) {
              // PREFILLED, NOT CLAMPED (D4). `changeAltitude` clamps only to the API's hard
              // envelope, never to the org ceiling — a station above the ceiling has to show
              // BOTH notices, because quietly lowering the number to a legal one would hand
              // back an altitude coverage is guaranteed to refuse, which is #469 wearing a fix.
              changeAltitude(band.stationAltitudeM, 'prefill');
            }
          } catch {
            // The read failed, and unlike "no coverage" that is something the operator is told —
            // see `stationBandGap`.
            setStationBandGap('read_failed');
          }
        }
        openReviewStep();
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'Failed to load mission'));
      } finally {
        setMissionLoadBusy(false);
      }
    },
    [plan, openReviewStep, changeAltitude],
  );

  const backToTarget = () => {
    setAltitudeOpen(false);
    setStep('target');
    if (launchMode === 'point') {
      plan.beginPoint(altitudeMeters);
    } else if (launchMode === 'route') {
      if (!plan.planOnMap) plan.togglePlanOnMap();
    }
    // mission: nothing to re-arm — the list is still in `missions`.
  };

  const saveAsDefault = async () => {
    if (!onSaveDefaultAltitude) return;
    setSavingDefault(true);
    try {
      await onSaveDefaultAltitude(altitudeMeters);
      toast.success(`Default altitude set to ${formatAltitude(altitudeMeters, unit)}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to save default altitude'));
    } finally {
      setSavingDefault(false);
    }
  };

  /**
   * @param successLabel  what to say when the aircraft HAS been commanded.
   * @param acceptedLabel what to say when the bay is open and it has NOT been commanded yet.
   *
   * TWO WRITTEN STRINGS, not one derived from the other. This used to be
   * `successLabel.replace(/dispatched/i, 'accepted')`, which is silent when it does not match:
   * a future label worded "Launch sent" would keep the word "sent" on a loading toast for an
   * aircraft sitting in a bay. Making both explicit is what lets TypeScript require the second.
   */
  async function dispatchLaunch(
    body: DockMissionRequest,
    successLabel: string,
    acceptedLabel: string,
  ) {
    setBusy(true);
    // Captured inside the try, acted on outside it. Notifying the COP and closing
    // the card are consequences of a launch that already happened; running them
    // inside the try would let a throw from either report an airborne aircraft as
    // a failed launch — and leave this card armed, inviting a second launch at a
    // drone already in the air.
    let launched: { droneId: string | null; missionId: string | null } | null = null;
    try {
      const res = await api.post<{ data?: DockMissionResponse }>(
        `/api/docks/${dockId}/mission`,
        targetDroneId ? { ...body, drone_id: targetDroneId } : body,
      );
      // Hand the caller the exact sortie this launch created. The aircraft can
      // hold more than one active mission — a planned one, or a previous sortie
      // still closing out — so its drone id alone cannot identify what is flying
      // now, and only this response knows. `mission_id` is null when the
      // backend's own bookkeeping failed (it deliberately does not fail a launch
      // the GO/NO-GO gate cleared): the launch is still real, and the COP is told
      // so explicitly rather than being left to guess from the aircraft.
      //
      // The fields are read defensively because a 202 body that is well-formed
      // but missing them still means the aircraft is airborne. This does NOT
      // cover a body that cannot be parsed at all — api.ts rejects on that before
      // returning, and the catch below will call it a failed launch. See the
      // known-gap note in the PR.
      launched = {
        droneId: res?.data?.drone_id ?? targetDroneId ?? assignedDrone?.drone_id ?? null,
        missionId: res?.data?.mission_id ?? null,
      };
      // Anything the launch did NOT refuse on, said out loud — the coverage-station altitude
      // mismatch (#469) and the airspace conflicts the gate let through. This card was the one
      // launch door that dropped `advisories` on the floor, which for #469 would mean the
      // server's backstop — the check that still runs when the card's own coverage read failed —
      // never reached anyone.
      //
      // ONE TOAST, WARNING-STYLED, exactly as CopMissionsPanel does it and for the reason stated
      // there: under `richColors` a success toast prints its description in the green this
      // product reserves for "all good", so a caution ridden on a success would be typeset as
      // reassurance.
      // WHAT THIS TOAST MAY CLAIM DEPENDS ON WHAT THE SERVER ANSWERED (#418).
      //
      // On `accepted` the launch has been ACCEPTED, not dispatched: the bay is open and the
      // aircraft has been commanded nothing yet. Saying "dispatched" here would be the card
      // asserting a takeoff that has not happened, and on the 2026-09-14 prod failure it would
      // have said so 15 s before `start_mission` was refused. So it opens a LOADING toast keyed
      // by the launch, and `useCopLaunchProgress` carries that same toast through the
      // home-point hold to its real outcome - success or refusal - after this card has closed.
      //
      // GATED ON `status`, NOT ON THE PRESENCE OF `launch_id`. Both are sent, including on the
      // synchronous `dispatched` body an AIRBORNE launch through this door still gets - that
      // aircraft is already flying, and branching on the id alone would pin a "bay open,
      // preparing the aircraft" spinner to a sortie in the air. That is this feature's own
      // failure mode, inverted.
      const launchId = res?.data?.launch_id;
      const accepted = res?.data?.status === 'accepted' && Boolean(launchId);
      const advisory = launchAdvisoryNote(res?.data?.advisories);
      if (accepted && launchId) {
        // Registered BEFORE the toast, so no frame that arrives in between is filtered out as
        // somebody else's launch.
        registerOwnLaunch(launchId);
        toast.loading(acceptedLabel, {
          id: launchToastId(launchId),
          description: 'Bay open - preparing the aircraft',
        });
      } else if (advisory) {
        // ONE TOAST, WARNING-STYLED: under `richColors` a success toast prints its description
        // in the green this product reserves for "all good", so a caution ridden on a success
        // would be typeset as reassurance.
        toast.warning(successLabel, { description: advisory, duration: 10_000 });
      } else {
        toast.success(successLabel);
      }
    } catch (e) {
      // The dock never confirmed its bay. That is not necessarily a fault: a
      // board that does not report tray position at all (M-001, 2026-08-11)
      // makes every sortie unlaunchable. Ask the operator, who can see the bay,
      // instead of failing them into a dead end.
      // Duck-typed on `code`, not `instanceof`: class identity does not survive
      // a module mock (or a bundle boundary), and an exception thrown from
      // inside this catch would swallow the resync below.
      if (typeof e === 'object' && e !== null && (e as { code?: unknown }).code === 'bay_unconfirmed') {
        // Still resync: the backend cancels the mission row it created BEFORE
        // it waits on the bay, so skipping this would leave the panel showing a
        // cancelled sortie's waypoints while the aircraft sits in the dock.
        onLaunchFailed?.();
        setBayPrompt({
          body,
          successLabel,
          acceptedLabel,
          reason: getApiErrorMessage(e, 'The dock did not confirm its bay is open.'),
        });
        setBusy(false);
        return;
      }
      toast.error(getApiErrorMessage(e, 'Launch failed'));
      // The backend writes the mission row and sets it in_progress BEFORE it waits
      // on the bay, and that wait can run to 60s — long enough for a poll to cache
      // the mission as active. A refused launch cancels that row, so without this
      // resync the panel would keep showing a cancelled mission's waypoints, and
      // the command bar would keep offering mission actions, for an aircraft still
      // sitting in a closed bay.
      onLaunchFailed?.();
    } finally {
      setBusy(false);
    }
    if (launched) {
      onLaunched?.(launched.droneId, launched.missionId);
      onClose();
    }
  }

  async function launchToPoint() {
    const point = plan.waypoints[0];
    if (!point) return;
    await dispatchLaunch(
      {
        // No mission_name: the backend names the sortie from mode + dock +
        // launch time, which it alone knows authoritatively (this card has the
        // dock id, not its name, and the launch clock must be the server's).
        launch_mode: 'point',
        waypoints: [{ seq: 0, lat: point.lat, lng: point.lng, alt: altitudeMeters, speed: point.speed, action: 'goto' }],
        finish_action: 'hover',
      },
      `Point launch dispatched · ${formatAltitude(altitudeMeters, unit)} AGL`,
      `Point launch accepted · bay open · ${formatAltitude(altitudeMeters, unit)} AGL`,
    );
  }

  async function launchTakeoff() {
    // Prefer the aircraft's live fix; fall back to the dock registry position
    // (SkyPulse is GPS-dark under a closed lid). Never guess.
    const hasFix = assignedDrone
      && Number.isFinite(assignedDrone.latitude) && Number.isFinite(assignedDrone.longitude)
      && (assignedDrone.latitude !== 0 || assignedDrone.longitude !== 0);
    const origin = hasFix
      ? { lat: assignedDrone.latitude, lng: assignedDrone.longitude }
      : dockPosition;
    if (!origin) {
      toast.error('No takeoff origin — no aircraft fix and no dock location set');
      return;
    }
    // Two waypoints ~2 m apart: DJI waylines need at least two placemarks,
    // and the nudge keeps this a climb-and-hover, not a transit.
    const NUDGE_DEG = 0.00002;
    await dispatchLaunch(
      {
        launch_mode: 'point',
        waypoints: [
          { seq: 0, lat: origin.lat, lng: origin.lng, alt: altitudeMeters, action: 'goto' },
          { seq: 1, lat: origin.lat + NUDGE_DEG, lng: origin.lng, alt: altitudeMeters, action: 'goto' },
        ],
        finish_action: 'hover',
      },
      `Takeoff dispatched · climb to ${formatAltitude(altitudeMeters, unit)} AGL`,
      `Takeoff accepted · bay open · climb to ${formatAltitude(altitudeMeters, unit)} AGL`,
    );
  }

  async function launchRoute() {
    const savedMission = launchMode === 'mission' && selectedMission ? selectedMission : null;
    await dispatchLaunch(
      {
        // A saved mission keeps the name the operator gave it; a freshly drawn
        // route is named by the backend (see launchToPoint).
        ...(savedMission
          ? { mission_name: savedMission.name, mission_id: savedMission.id }
          : { launch_mode: 'route' as const }),
        waypoints: plan.waypoints.map((w, i) => ({
          seq: i,
          lat: w.lat,
          lng: w.lng,
          alt: altitudeMeters,
          ...(Number.isFinite(w.speed) ? { speed: w.speed } : {}),
          action: w.action,
          ...(w.action === 'hover' ? { hold_s: w.hold_s } : {}),
        })),
        rth_altitude_m: altitudeProfile.rth_altitude_m,
        // A SAVED MISSION DOES NOT GET TO BE TOLD HOW IT ENDS (#379).
        //
        // This used to send `finish_action: 'return_to_dock'` for both branches. For a saved
        // mission that was a lie the aircraft obeyed: a HOVER / LOITER flew its route and then
        // went home, which is the one thing its type says it does not do. The mission's TYPE
        // decides that now, server-side, in the same place the COP mission panel decides it —
        // so the same mission finishes the same way whichever button was pressed.
        //
        // What the card still owns is the ALTITUDE the operator dialled, which replaces the
        // mission's stored per-waypoint values. It has to travel as its own field because the
        // server flies the STORED route and never looks at `waypoints` above for this branch.
        // (`missionPlannedRange` below shows the operator the spread being overridden.)
        ...(savedMission
          ? { altitude_m: altitudeMeters }
          : { finish_action: 'return_to_dock' as const }),
      },
      `${launchMode === 'mission' ? 'Mission' : 'Route launch'} dispatched · ${formatAltitude(altitudeMeters, unit)} AGL`,
      `${launchMode === 'mission' ? 'Mission' : 'Route launch'} accepted · bay open · ${formatAltitude(altitudeMeters, unit)} AGL`,
    );
  }

  const launchDisabled = !isOnline || busy;
  const blockReason =
    launchMode === 'takeoff' ? null : launchMode === 'point' ? pointBlockReason : routeBlockReason;
  const altitudeLabel = formatAltitude(altitudeMeters, unit);
  const isDefaultAltitude = altitudeMeters === altitudeProfile.default_m;
  // Evaluated on the card, not inside the collapsed picker: a one-click launch
  // above the ceiling must never dispatch with the warning hidden behind a
  // disclosure the operator had no reason to open.
  const ceilingNotice = evaluateCeiling(altitudeMeters, { ...altitudeProfile, unit }, ceilingAuthority);
  const aboveCeiling = ceilingNotice.status === 'over';
  // Derived rather than cleared on every mode switch: the band belongs to a SELECTED MISSION, so
  // point, route and takeoff launches must not be judged against the last mission's station even
  // though `stationBand` still holds it (#469).
  const stationNotice = evaluateStationAltitude(
    altitudeMeters,
    launchMode === 'mission' && selectedMission ? stationBand : null,
    unit,
  );
  const offStation = stationNotice.status === 'off_station';
  // Shown only while the prefilled value is still the one in the field and still on-station —
  // once the operator moves off it, the amber notice above is the louder and more useful line
  // and this would just be a second sentence about the same number.
  const prefillNotice = altitudePrefilled && !offStation && stationBand !== null
    ? `ALT set to this mission's ${formatAltitude(stationBand.stationAltitudeM, unit)} station altitude.`
    : null;
  const notReady = !isOnline || blockReason !== null;
  const configUnavailable = altitudeProfileStatus === 'error';
  // A WARNING endurance verdict colours the button the way off-station does (#753). D1 forbids
  // refusing the launch on it, not carrying it on the control: an amber block above a green LAUNCH
  // is the 02:00 click-past this card already refuses for the ceiling and the station altitude.
  // Guarded the same way as the notice - it belongs to the selected mission and its aircraft.
  const enduranceShort = launchMode === 'mission' && selectedMission !== null
    && enduranceLoad?.missionId === selectedMission.id
    && stationEnduranceWarningHead(stationEnduranceFor(selectedMission.id, enduranceDroneId)) !== null;
  const launchWarned = aboveCeiling || offStation || enduranceShort;
  // The label names every warning that holds, in the card's order: ceiling, station, rotation.
  const launchWarningTerms = [
    aboveCeiling ? 'above ceiling' : null,
    offStation ? (aboveCeiling ? 'off station' : 'off station alt') : null,
    enduranceShort ? 'short for rotation' : null,
  ].filter((term): term is string => term !== null);

  /**
   * A loaded mission carries its own per-waypoint altitudes, and dispatch
   * replaces every one of them with the card's value. Surfacing the planned
   * spread makes that an informed override instead of a silent flattening.
   */
  const missionPlannedRange = (() => {
    if (launchMode !== 'mission' || plan.waypoints.length === 0) return null;
    const alts = plan.waypoints.map((w) => w.alt).filter((a) => Number.isFinite(a));
    if (alts.length === 0) return null;
    const low = Math.min(...alts);
    const high = Math.max(...alts);
    if (low === altitudeMeters && high === altitudeMeters) return null;
    return low === high
      ? `${toDisplay(low, unit)} ${unit}`
      : `${toDisplay(low, unit)}–${toDisplay(high, unit)} ${unit}`;
  })();

  return (
    <>
      {/* The dock could not confirm its bay. The operator can see it; the dock
          cannot report it. Never auto-proceed - this is the last gate before an
          aircraft is commanded to fly out of a box. */}
      {bayPrompt && (
        <div className="absolute inset-x-0 bottom-4 z-30 flex justify-center px-3" style={centredOverVisibleMap}>
          <div className={CARD_CLASS}>
            <OverlayHeader title={`Bay unconfirmed · ${droneLabel}`} onCancel={() => setBayPrompt(null)} />
            <div className="space-y-2.5 p-3">
              <p className="text-[10px] leading-relaxed text-status-amber-fg">
                {bayPrompt.reason}
              </p>
              <p className="text-[10px] leading-relaxed text-muted-foreground">
                Look at the dock. Only confirm if the bay is FULLY open and the
                aircraft is clear to leave it. SHOVRON cannot verify this.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setBayPrompt(null)}
                  className="flex-1 border border-subtle text-[10px] uppercase"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    const prompt = bayPrompt;
                    setBayPrompt(null);
                    void dispatchLaunch(
                      { ...prompt.body, bay_confirmed: true },
                      prompt.successLabel,
                      prompt.acceptedLabel,
                    );
                  }}
                  className="flex-1 border border-status-amber/40 bg-status-amber/15 text-[10px] uppercase text-status-amber-fg hover:bg-status-amber/25"
                >
                  Bay is open · launch
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 'target' && launchMode === 'route' && routeError && (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-3" style={centredOverVisibleMap}>
          <div className={CARD_CLASS}>
            <OverlayHeader title={`${MODE_LABEL.route} · ${droneLabel}`} onCancel={onClose} />
            <div className="space-y-2 p-3">
              <p className="text-[11px] text-status-red-fg">{routeError}</p>
              <Button
                size="sm"
                onClick={() => plan.togglePlanOnMap()}
                className="w-full bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 text-[10px] uppercase"
              >
                <Route className="size-3" /> Resume drawing
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 'target' && launchMode === 'mission' && (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-3" style={centredOverVisibleMap}>
          <div className={CARD_CLASS}>
            <OverlayHeader title={`${MODE_LABEL.mission} · ${droneLabel}`} onCancel={onClose} />
            <div className="max-h-72 overflow-y-auto p-2">
              {missionsLoading ? (
                <p className="px-2 py-2 text-[10px] text-muted-foreground">Loading missions…</p>
              ) : missionsLoadError ? (
                /* A failed load is not an empty queue. The toast that fired with it is gone in
                   ~4 s and the overlay outlives it, so without this the operator is left reading
                   "no missions" as the answer — and the only recovery was to close the overlay
                   and reopen it, which is not discoverable from a sentence that claims success. */
                <div className="space-y-1.5 px-2 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-status-amber-fg">
                    Mission list unavailable
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    The saved missions could not be loaded.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => void refreshMissions()}
                    className="h-6 w-full border border-primary/30 bg-primary/20 text-[10px] uppercase text-primary hover:bg-primary/30"
                  >
                    Retry
                  </Button>
                </div>
              ) : missions.length === 0 ? (
                /* NOT "no missions yet": the fetch above asks for three statuses, so an org with
                   a year of flown sorties and nothing currently planned lands here. Telling that
                   operator the platform has no record of any mission reads as data loss, and
                   sends them to look for something that is in fact in History. Mirrors
                   `emptyBoardMessage` on the missions board, which draws the same distinction. */
                <p className="px-2 py-2 text-[10px] text-muted-foreground">
                  No launchable missions. Completed and cancelled missions are in History.
                </p>
              ) : (
                <div className="divide-y divide-border">
                  {missions.map((m) => {
                    // The server's own refusal, asked BEFORE the operator arms a launch rather
                    // than reported after it. A blocked row stays visible — an operator looking
                    // for a mission they know exists must find it and read why it cannot fly,
                    // not be left searching a list it silently vanished from.
                    // The AIRCRAFT, not the door. This card launches out of a bay, so its
                    // airframe is dock-stationed and an ORBIT is refused on it - but the rule
                    // the server applies is `drones.dock_id` plus the command route, so the
                    // aircraft is what gets asked. Said on the row rather than found at LAUNCH.
                    // `{ dock_id: dockId }` rather than the live drone row: `assignedDrone` is
                    // TELEMETRY and carries neither `dock_id` nor `command_route`. It needs
                    // neither - this card launches out of THIS dock's bay, so the aircraft is
                    // stationed in it by construction, which is the fact the orbit refusal turns
                    // on. The command-route half is moot here because the dock check answers first.
                    const blocked = missionLaunchBlockReason(m, { aircraft: { dock_id: dockId } });
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => void loadMission(m)}
                        disabled={missionLoadBusy || blocked !== null}
                        className="flex w-full flex-col gap-0.5 px-2 py-2 text-left transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        <span className="flex w-full items-center gap-2">
                          <span className="flex-1 min-w-0 truncate text-[11px] uppercase text-foreground">
                            {m.name}
                          </span>
                          {typeof m.waypoint_count === 'number' && (
                            <span className="shrink-0 font-mono tabular-nums text-[9px] text-muted-foreground">{m.waypoint_count} wp</span>
                          )}
                        </span>
                        {/* Not a toast. The reason sits on the row that carries it, for as long
                            as the row is on screen, and inside the button so it is part of the
                            control's own accessible name rather than a separate description. */}
                        {blocked && (
                          <span className="text-[9px] uppercase leading-tight text-status-amber-fg">
                            {blocked}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* `!bayPrompt`: the bay prompt is the SAME anchor, width and background as this
          card, and nothing unmounts this one on a refused launch (setStep is never
          called from dispatchLaunch). Rendered together, the z-30 prompt sat under
          this z-50 card whose CARD_CLASS carries pointer-events-auto, so the only
          escape from a bay_unconfirmed refusal was invisible AND unclickable — prod
          logged that override zero times in the container's life while the 409 fired
          twice. The prompt supersedes the review card; it is the more urgent gate. */}
      {step === 'review' && !bayPrompt && (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-50 flex justify-center px-3" style={centredOverVisibleMap}>
          <div className={CARD_CLASS}>
            <OverlayHeader title={`${MODE_LABEL[launchMode]} · ${droneLabel}`} onCancel={onClose} />
            <div className="space-y-2.5 p-3">
              <div
                className={`space-y-1.5 rounded border p-2.5 ${
                  notReady
                    ? 'border-status-red/25 bg-status-red/5'
                    : 'border-status-green/20 bg-status-green/5'
                }`}
              >
                <div className="flex items-center gap-2">
                  {launchMode === 'route' ? (
                    <Route className={`size-3.5 ${notReady ? 'text-status-red-fg' : 'text-status-green-fg'}`} />
                  ) : (
                    <MapPinned className={`size-3.5 ${notReady ? 'text-status-red-fg' : 'text-status-green-fg'}`} />
                  )}
                  {/* One verdict, not a green banner over a red blocker. */}
                  <span className={`text-[10px] uppercase ${notReady ? 'text-status-red-fg' : 'text-status-green-fg'}`}>
                    {!isOnline ? 'Dock offline — launch unavailable' : blockReason ?? 'Ready to launch'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-[10px] text-foreground">
                  <span className="col-span-2 truncate">DRONE {droneLabel}</span>
                  {launchMode === 'point' && plan.waypoints[0] && (
                    <>
                      <span className="font-mono tabular-nums">LAT {plan.waypoints[0].lat.toFixed(6)}</span>
                      <span className="font-mono tabular-nums">LNG {plan.waypoints[0].lng.toFixed(6)}</span>
                    </>
                  )}
                  {launchMode !== 'point' && (
                    <>
                      <span>{launchMode === 'mission' ? (selectedMission?.name ?? 'MISSION') : 'ROUTE'}</span>
                      <span className="font-mono tabular-nums">{plural(plan.waypoints.length, TERMS.waypoint, TERMS.waypoints)}</span>
                      <span className="font-mono tabular-nums">{formatDistance(plan.totalDistanceM)}</span>
                      <span className="font-mono tabular-nums">EST {formatDuration(plan.estimatedTimeS)}</span>
                    </>
                  )}
                </div>
                {/* The altitude is the parameter this whole card exists to set —
                    it reads at a glance, not at the same weight as LAT/LNG. */}
                <div className="flex items-baseline gap-1.5 border-t divider pt-1.5">
                  <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Alt</span>
                  <span className={`font-mono text-lg font-semibold leading-none tabular-nums ${aboveCeiling ? 'text-status-amber-fg' : 'text-foreground'}`}>
                    {altitudeLabel}
                  </span>
                  <span className="text-[9px] uppercase tracking-wider text-muted-foreground">AGL from dock pad</span>
                </div>
                {missionPlannedRange && (
                  <p className="text-[9px] leading-snug text-status-amber-fg">
                    Overrides this mission&apos;s planned {missionPlannedRange} on every waypoint.
                  </p>
                )}
              </div>

              {altitudeOpen && (
                <AltitudeControl
                  id={ALTITUDE_PANEL_ID}
                  profile={altitudeProfile}
                  authority={ceilingAuthority}
                  valueMeters={altitudeMeters}
                  onChange={changeAltitude}
                  unit={unit}
                  onUnitChange={setUnit}
                  showRth={launchMode !== 'point'}
                  disabled={busy}
                  /* Never offered for a PREFILLED value (#469). The prefill flips
                     `altitudeTouchedRef` the same way a typed value does, which made
                     `isDefaultAltitude` false and put "Make 66 ft the default for all operators"
                     in front of an admin who never chose 66 ft — it belongs to one mission's
                     station. One tired click and every future launch org-wide opens there. */
                  footer={onSaveDefaultAltitude && !isDefaultAltitude && !altitudePrefilled ? (
                    <div className="border-t divider pt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void saveAsDefault()}
                        disabled={savingDefault || busy}
                        className="w-full text-[9px] uppercase tracking-wider"
                      >
                        {savingDefault ? 'Saving…' : `Make ${altitudeLabel} the default for all operators`}
                      </Button>
                    </div>
                  ) : undefined}
                />
              )}

              {/* Announced, not just shown: crossing the ceiling while dragging
                  the slider has to reach an operator who isn't watching it. */}
              <div role="status" aria-live="polite">
                {ceilingNotice.message && (
                  <p
                    className={`flex items-start gap-1.5 rounded border px-2 py-1.5 text-[10px] leading-snug ${
                      aboveCeiling
                        ? 'border-status-amber/40 bg-status-amber/10 text-status-amber-fg'
                        : 'border-subtle text-muted-foreground'
                    }`}
                  >
                    <AlertTriangle className="mt-px size-3 shrink-0" aria-hidden="true" />
                    <span>{ceilingNotice.message}</span>
                  </p>
                )}
                {/* BOTH notices can stand at once (#469 D4) — they are different limits, and
                    suppressing either would hide a real constraint on the launch. Only
                    `message` is inside the live region: it is built from the band, so it does
                    not change while the operator moves the altitude. The varying gap and the
                    constant consequence sit below it, `aria-hidden`, so a drag from 66 ft to
                    400 ft does not announce thirty times over a control in active use. */}
                {stationNotice.message && (
                  <div className="mt-1 rounded border border-status-amber/40 bg-status-amber/10 px-2 py-1.5 text-status-amber-fg">
                    <p className="flex items-start gap-1.5 text-[10px] leading-snug">
                      <AlertTriangle className="mt-px size-3 shrink-0" aria-hidden="true" />
                      <span>
                        {stationNotice.message}
                        {stationNotice.gap && (
                          <span aria-hidden="true"> · {stationNotice.gap}</span>
                        )}
                      </span>
                    </p>
                    {stationNotice.consequence && (
                      <p aria-hidden="true" className="mt-1 pl-[18px] text-[9px] leading-snug opacity-80">
                        {stationNotice.consequence}
                      </p>
                    )}
                  </div>
                )}
                {/* The prefill moved ALT on its own, so it says so. Muted and unadorned, because
                    this is the SUCCESS path: without it the operator sees a field that has read
                    250 ft all month showing 66 ft for no stated reason, and the obvious
                    correction — setting it back — is incident #469 verbatim. */}
                {prefillNotice && (
                  <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
                    {prefillNotice}
                  </p>
                )}
                {/* A failed read is NOT the same as no coverage, and this card already says so
                    about its mission list. Silence here would tell the operator the altitude was
                    checked against the station when it never was.

                    Two sentences for the two reasons (#650): the first knows there is a station
                    and could not read it, the second does not know whether there is one. Both end
                    on the same clause, because the consequence for the launch is identical and
                    that clause is the part the operator has to act on.

                    GUARDED THE WAY `stationNotice` IS, and for the same reason it is derived
                    rather than cleared: the gap belongs to a SELECTED MISSION, and nothing resets
                    it when the operator switches to TO POINT with the overlay still mounted. A
                    point launch has no mission and no station, so without this the card would
                    carry a coverage warning at the moment of commit for a launch coverage has no
                    opinion about at all.

                    AMBER, LIKE `stationNotice`, NOT MUTED LIKE `prefillNotice`. "The check found a
                    problem" and "the check never ran" are the same size of fact for whoever is
                    about to press LAUNCH, and this used to be drawn at the weight of the success
                    line. #650 also makes this branch far commoner - during an `attached` lookup
                    fault EVERY mission in the picker takes it. */}
                {launchMode === 'mission' && selectedMission && stationBandGap && (
                  <p className="mt-1 flex items-start gap-1.5 rounded border border-status-amber/40 bg-status-amber/10 px-2 py-1 text-[10px] leading-snug text-status-amber-fg">
                    <AlertTriangle className="mt-px size-3 shrink-0" aria-hidden="true" />
                    <span>
                      {stationBandGap === 'read_failed'
                        ? COVERAGE_UNREAD_COPY.stationUnavailable
                        : COVERAGE_UNREAD_COPY.stationNotCheckable}
                    </span>
                  </p>
                )}
                {/* #753 - can this aircraft hold the station long enough to be relieved on
                    schedule. AMBER AND NEVER DISABLING: it is a judgement about the mission, not
                    airworthiness, so LAUNCH stays live and unrelabelled (D1).

                    "Checking" and "not checked" are MUTED, which is a deliberate departure from
                    `stationBandGap` above (D5). A missing station read is a rare fault; an
                    unprojectable aircraft is ordinary - no energy model, a swap dock with the
                    airframe powered down - and an amber line on every such launch would teach
                    operators to read past the one that matters. It is still a line, never silence.

                    Guarded like the station notices, because it belongs to the selected mission. */}
                {launchMode === 'mission' && selectedMission && enduranceLoad?.missionId === selectedMission.id && (
                  <StationEnduranceNotice state={stationEnduranceFor(selectedMission.id, enduranceDroneId)} />
                )}
              </div>

              {configUnavailable && (
                <p className="text-[10px] leading-snug text-status-amber-fg">
                  Org altitude settings unavailable — showing platform defaults, not your configuration.
                </p>
              )}

              <div className="grid grid-cols-[1fr_auto]">
                {/* Above the ceiling the button itself carries the state — the
                    same green control for a routine and an over-ceiling launch
                    is how a warning gets clicked past at 02:00.
                    Off-station carries it the same way, for the same reason (#469): the operator
                    is about to spend a whole sortie on a hold that cannot succeed, and a green
                    LAUNCH button is what made that look routine.
                    WHEN BOTH ARE TRUE THE LABEL NAMES BOTH. An earlier version let the ceiling
                    win the label outright, so an operator over both limits saw only the ceiling,
                    corrected to 400 ft, and only then discovered they were still 100 m
                    off-station — two sequential surprises under time pressure from a control
                    that had both facts in hand. A short-for-rotation verdict joins the same
                    label for the same reason (#753). */}
                <Button
                  size="sm"
                  disabled={launchDisabled || blockReason !== null}
                  onClick={() => void (launchMode === 'takeoff' ? launchTakeoff() : launchMode === 'point' ? launchToPoint() : launchRoute())}
                  className={`rounded-r-none border text-[10px] uppercase tracking-wider ${
                    launchWarned
                      ? 'bg-status-amber/15 hover:bg-status-amber/25 text-status-amber-fg border-status-amber/40'
                      : 'bg-status-green/15 hover:bg-status-green/25 text-status-green-fg border-status-green/30'
                  }`}
                >
                  {launchWarned ? <AlertTriangle className="size-3" /> : <Send className="size-3" />}
                  {busy
                    ? 'Dispatching…'
                    : launchWarningTerms.length > 0
                      ? `Launch ${launchWarningTerms.join(' · ')} @ ${altitudeLabel}`
                      : `Launch @ ${altitudeLabel} AGL`}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  aria-expanded={altitudeOpen}
                  aria-controls={ALTITUDE_PANEL_ID}
                  aria-label={altitudeOpen ? 'Alt — hide launch altitude controls' : 'Alt — change launch altitude'}
                  disabled={busy}
                  onClick={() => setAltitudeOpen((open) => !open)}
                  className="rounded-l-none text-[10px] uppercase"
                >
                  Alt
                  <ChevronDown className={`size-3 transition-transform ${altitudeOpen ? 'rotate-180' : ''}`} />
                </Button>
              </div>

              {/* Named for what it actually does per mode: in point mode it
                  clears the placed target, in route mode it resumes drawing. */}
              {launchMode !== 'mission' && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={backToTarget}
                  disabled={busy}
                  className="w-full text-[10px] uppercase tracking-wider text-muted-foreground"
                >
                  {launchMode === 'point' ? 'Re-place target' : 'Resume drawing'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {step === 'target' && (launchMode === 'point' || launchMode === 'route') && !routeError && (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-3" style={centredOverVisibleMap}>
          <button
            type="button"
            onClick={onClose}
            className="pointer-events-auto flex items-center gap-1.5 rounded-md border border-subtle bg-popover/90 px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground shadow-lg backdrop-blur-sm hover:text-foreground"
          >
            {launchMode === 'point' ? <Crosshair className="size-3" /> : <Route className="size-3" />}
            Cancel {MODE_LABEL[launchMode]}
          </button>
        </div>
      )}
    </>
  );
}
